import { gradeName } from "./grade-names";
/** A nutrition-service health report with the score details an admin needs to check it. */

const FACT_NAMES = { sugar: "total sugars", saturatedFat: "saturated fat", sodium: "sodium" };
const LIGHT_NAMES = { fat: "Fat", saturatedFat: "Sat. fat", sugar: "Sugars", salt: "Salt" };
const LEVEL_TEXT = { LOW: "Low", MEDIUM: "Medium", HIGH: "High", UNKNOWN: "?" };
const CONFIDENCE_TEXT = { HIGH: "High", MEDIUM: "Medium", LOW: "Low" };

const NUTRI_COLORS = { A: "#038141", B: "#85BB2F", C: "#FECB02", D: "#EE8100", E: "#E63E11" };

const WHO_NUTRIENTS = {
  totalFat: ["Total fat", "g"],
  saturatedFat: ["Saturated fat", "g"],
  totalSugars: ["Total sugars", "g"],
  addedSugars: ["Added sugars", "g"],
  sodium: ["Sodium", "mg"],
  energy: ["Energy", "kcal"],
};
const WHO_STATUS = {
  PASS: ["Within WHO limits", "#34A853"],
  FAIL: ["Above WHO limits", "#D93025"],
  INCOMPLETE: ["WHO check incomplete", "#9AA0A6"],
  NOT_COVERED: ["Not covered by the WHO model", "#9AA0A6"],
  UNKNOWN_CATEGORY: ["Set the food category to check WHO limits", "#9AA0A6"],
};

/** WHO South-East Asia Region nutrient profile model (2017): each threshold for the product's category. */
function WhoTable({ who }) {
  const [label, color] = WHO_STATUS[who.status];
  return (
    <div className="nutri-who">
      <strong style={{ color }}>{label}</strong>
      {who.category && <span className="nutri-muted"> · WHO SEARO category {who.category.code}: {who.category.name}</span>}
      {who.checks.length > 0 && (
        <table className="table table-sm mb-1">
          <thead>
            <tr><th>Per 100 g</th><th>Product</th><th>WHO limit</th><th /></tr>
          </thead>
          <tbody>
            {who.checks.map((c) => {
              const [name, unit] = WHO_NUTRIENTS[c.nutrient];
              return (
                <tr key={c.nutrient}>
                  <td>{name}</td>
                  <td>{c.value === null ? "not entered" : `${c.value} ${unit}`}</td>
                  <td>{c.limit} {unit}</td>
                  <td style={{ color: c.exceeds ? "#D93025" : c.exceeds === false ? "#34A853" : "#9AA0A6" }}>
                    {c.exceeds ? "✕ over" : c.exceeds === false ? "✓" : "?"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {who.exclusions.map((e) => <div key={e} className="text-danger">✕ {e}</div>)}
    </div>
  );
}

function GradeCard({ report, combo = false }) {
  const rated = report.eatProteinScore !== null;
  const { positives, penalties, caps, floor, base } = report.breakdown;

  return (
    <div className="nutri-card">
      <div className="nutri-badge" style={{ background: report.color }}>
        <div className="nutri-badge-score">{rated ? report.eatProteinScore : "–"}</div>
        <div>
          <div className="nutri-badge-grade">{rated ? gradeName(report.grade) : "NOT YET RATED"}</div>
          <div>{report.label}</div>
          <small>{report.advice}</small>
        </div>
      </div>

      {report.verdict && (
        <p className={`nutri-verdict nutri-verdict-${report.verdict.code.toLowerCase()}`}>
          {report.verdict.summary}
        </p>
      )}

      {report.confidence && (
        <p className="nutri-muted mb-2">
          Data confidence: <strong>{CONFIDENCE_TEXT[report.confidence.level]}</strong>
          {report.confidence.reasons.length > 0 && ` — ${report.confidence.reasons.join("; ")}`}
        </p>
      )}

      {report.missingFacts.length > 0 && combo && (
        <p className="nutri-missing">
          The pack can't be graded yet: {report.missingFacts.map((f) => FACT_NAMES[f] ?? f).join(", ")} missing on
          some of its products. Use “Fill in missing” next to those products in the pack contents; the pack is then
          graded automatically.
        </p>
      )}
      {report.missingFacts.length > 0 && !combo && (
        <p className="nutri-missing">
          Enter {report.missingFacts.map((f) => FACT_NAMES[f] ?? f).join(", ")} from the pack to give this
          product a grade. The WHO limits and Nutri-Score cannot be checked without them.
        </p>
      )}

      {report.who && <WhoTable who={report.who} />}

      <div className="nutri-chips">
        {report.nutriScore && (
          <span className="nutri-chip" style={{ background: NUTRI_COLORS[report.nutriScore.grade] }}>
            Nutri-Score {report.nutriScore.grade}
            {report.nutriScore.basis === "CALCULATED" ? "" : " (pack, verified)"}
          </span>
        )}
        {report.nova && (
          <span className="nutri-chip nutri-chip-plain" title={report.nova.label}>
            NOVA {report.nova.group}
            {report.nova.estimated ? " (auto)" : ""}
          </span>
        )}
        <span className="nutri-chip" style={{ background: report.additives.color }}>
          {report.additives.label}
        </span>
      </div>

      <div className="nutri-lights">
        {Object.entries(report.trafficLights).map(([key, light]) => (
          <div key={key} className="nutri-light" style={{ borderColor: light.color }}>
            <strong>{LIGHT_NAMES[key]}</strong>
            <span style={{ color: light.color }}>{LEVEL_TEXT[light.level]}</span>
            <small>{light.per100 === null ? "not entered" : `${light.per100} g`}</small>
          </div>
        ))}
      </div>

      {report.highlights.length > 0 && (
        <ul className="nutri-list nutri-good">
          {report.highlights.map((h) => <li key={h}>{h}</li>)}
        </ul>
      )}
      {report.warnings.length > 0 && (
        <ul className="nutri-list nutri-bad">
          {report.warnings.map((w) => <li key={w}>{w}</li>)}
        </ul>
      )}

      {report.ingredients && (
        <div className="nutri-ingredients">
          <strong>Ingredients</strong>
          {report.ingredients.length > 0 ? (
            <p>{report.ingredients.map((i) => i.name).join(", ")}</p>
          ) : (
            <p className="nutri-muted">Not entered yet. Add the ingredient list from the pack.</p>
          )}
        </div>
      )}

      {report.additives.items.length > 0 && (
        <p className="nutri-muted">
          Additives: {report.additives.items.map((a) => `${a.name ?? a.code} (${a.concern.toLowerCase()})`).join(", ")}
        </p>
      )}

      <p className="nutri-muted">
        Protein {report.protein.per100} g / 100 g
        {report.protein.perServing !== null && ` · ${report.protein.perServing} g per serving`}
        {report.protein.isComplete !== null && ` · ${report.protein.isComplete ? "complete" : "incomplete"} protein`}
        {` · ${report.protein.dailyContributionPct}% of daily protein`}
      </p>

      <details className="nutri-breakdown">
        <summary>How the score is calculated</summary>
        <table className="table table-sm">
          <tbody>
            <tr><td>Starting points</td><td>{base}</td></tr>
            {positives.map((p) => (
              <tr key={p.code}><td>{p.reason}</td><td className="text-success">+{p.points}</td></tr>
            ))}
            {penalties.map((p) => (
              <tr key={p.code}><td>{p.reason}</td><td className="text-danger">−{p.points}</td></tr>
            ))}
            {caps.map((c) => (
              <tr key={c.code}><td>{c.reason}</td><td>max {c.maxScore}</td></tr>
            ))}
            {floor && <tr><td>{floor.reason}</td><td>min {floor.minScore}</td></tr>}
          </tbody>
        </table>
        <small className="nutri-muted">
          {report.foodCategory &&
            `Category ${report.foodCategory.name}${report.foodCategory.inferred ? " (guessed)" : ""} · `}
          Data completeness {report.dataCompleteness}% · algorithm {report.scoreVersion}
        </small>
      </details>
    </div>
  );
}

export default GradeCard;
